import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { chainsApi } from '@/lib/api/client';
import type { CreateChainRequest } from '@steward/shared';
import toast from 'react-hot-toast';

export const chainKeys = {
  all: ['chains'] as const,
  lists: () => [...chainKeys.all, 'list'] as const,
  list: (filters: { status?: string; symbol?: string }) => [...chainKeys.lists(), filters] as const,
  details: () => [...chainKeys.all, 'detail'] as const,
  detail: (id: string) => [...chainKeys.details(), id] as const,
  history: (id: string) => [...chainKeys.detail(id), 'history'] as const,
  orphans: () => [...chainKeys.all, 'orphans'] as const,
  openPnl: () => [...chainKeys.all, 'open-pnl'] as const,
};

export function useChains(params?: { status?: string; symbol?: string }) {
  return useQuery({
    queryKey: chainKeys.list(params || {}),
    queryFn: () => chainsApi.list({ ...params, limit: 500 }),
    select: (data) => data.chains,
  });
}

export function useChain(id: string) {
  return useQuery({
    queryKey: chainKeys.detail(id),
    queryFn: () => chainsApi.get(id),
    select: (data) => data.chain,
    enabled: !!id,
  });
}

export function useChainHistory(id: string) {
  return useQuery({
    queryKey: chainKeys.history(id),
    queryFn: () => chainsApi.getHistory(id),
    enabled: !!id,
  });
}

export function useCreateChain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateChainRequest) => chainsApi.create(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: chainKeys.lists() });
      toast.success('Trade created successfully');
      return data.chain;
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create trade');
    },
  });
}

export function useUpdateChain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { notes?: string; tags?: string[] } }) =>
      chainsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: chainKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: chainKeys.lists() });
      toast.success('Trade updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update trade');
    },
  });
}

export function useDeleteChain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => chainsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chainKeys.lists() });
      toast.success('Trade deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete trade');
    },
  });
}

export function useOpenPnl(enabled = true) {
  return useQuery({
    queryKey: chainKeys.openPnl(),
    queryFn: () => chainsApi.openPnl(),
    select: (data) => data.pnl,
    enabled,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 2 * 60 * 1000, // 2 minutes
  });
}

export function useOrphanChains() {
  return useQuery({
    queryKey: chainKeys.orphans(),
    queryFn: () => chainsApi.getOrphans(),
  });
}

export function useUnlinkPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chainId, positionId }: { chainId: string; positionId: string }) =>
      chainsApi.unlinkPosition(chainId, positionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chainKeys.all });
      toast.success('Position unlinked');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to unlink position');
    },
  });
}

export function useLinkPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ targetChainId, positionId }: { targetChainId: string; positionId: string }) =>
      chainsApi.linkPosition(targetChainId, positionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chainKeys.all });
      toast.success('Position linked to chain');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to link position');
    },
  });
}
